import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getDiffTimeDDHHmm } from "@/core/helpers/getDiffTimeDDHHmm";
import { Document5Icon, Exit3Icon, QuestionIcon, Star3Icon, Wallet3Icon } from "@/shared/icons";
import { TriangleArrowLeftIcon } from "@/shared/icons/TriangleArrowLeftIcon";
import { Button } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { ParticipantRow } from "@/domains/faction/constants/participantRow";
import { factionsList } from "@/domains/faction/helpers/factionsList";
import FactionExperience from "@/domains/faction/components/FactionExperience";
import FactionInfoBlock from "@/domains/faction/components/FactionInfoBlock";
import FactionParticipantCard from "@/domains/faction/components/FactionParticipantCard";

type Props = {
  faction: FactionEnum;
  rowInfo?: ParticipantRow;
  onBack: () => void;
  modals: ReactNode;
  actionsList: Array<() => void>;
  onDismiss: () => void;
  onUpRank: () => void;
};

export default function FactionPlayerInfo({
  faction,
  rowInfo,
  onBack,
  modals,
  actionsList,
  onDismiss,
  onUpRank,
}: Props) {
  const { t } = useTranslation("faction");

  const actionButtons = useMemo(
    () => [
      t("faction.toUp"),
      t("faction.toDown"),
      t("faction.giveBonus"),
      t("faction.changePosition"),
      t("faction.changeDepartment"),
      t("faction.addInBlackList"),
    ],
    [t],
  );

  return (
    <div className="flex size-full gap-6 p-6">
      <div className="w-64">
        <div className="flex items-center mb-3.5">
          <Button onClick={onBack} variant="solid" icon className="size-8 bg-dark-800">
            <TriangleArrowLeftIcon className="size-2.5" />
          </Button>
          <p className="ml-2.5 text-14 font-bold leading-none uppercase">{t("faction.goBack")}</p>
        </div>
        <FactionParticipantCard
          src="./img/faction/avatar.png"
          id={rowInfo?.characterId}
          name={`${rowInfo?.firstName} ${rowInfo?.lastName}`}
          rank={rowInfo?.rankName}
          position={rowInfo?.positionName}
          className="mb-3.5"
          textClassName="mt-4 mb-3.5"
        />
        <div className="flex flex-col gap-3.5">
          <FactionInfoBlock
            title={t("faction.tasks")}
            description={t("faction.tooltip.3.description")}
            count={90850}
            days={7}
            Icon={QuestionIcon}
            dataMzAccent={factionsList[faction].dataMzAccent}
          />
          <FactionInfoBlock
            title={t("faction.factionPoints")}
            description={t("faction.tooltip.4.description")}
            count={90850}
            days={5}
            Icon={Star3Icon}
            dataMzAccent={factionsList[faction].dataMzAccent}
          />
          <FactionInfoBlock
            title={t("faction.contributionToTheBudget")}
            description={t("faction.tooltip.5.description")}
            count={10000222}
            days={3}
            Icon={Wallet3Icon}
            dataMzAccent={factionsList[faction].dataMzAccent}
            isRuble
          />
        </div>
      </div>

      <div className="flex flex-col grow">
        <div className="flex items-center mt-1.5 mb-5 px-1">
          <Document5Icon className="size-5 text-white/30" />
          <p className="ml-2.5 text-14 font-bold leading-none uppercase">{t("faction.dossier")}</p>
        </div>
        <div className="grid grid-cols-3 gap-y-3.5 gap-x-4">
          <div className="p-4 bg-dark-800 rounded-[0.4375rem]">
            <p className="mb-3 text-14 font-medium text-white/55">{t("faction.timeInFaction")}</p>
            <p className="text-20 font-bold">
              {getDiffTimeDDHHmm(
                rowInfo?.createdAt,
                t("faction.YY"),
                t("faction.MM"),
                t("faction.DD"),
                t("faction.HH"),
                t("faction.mm"),
              )}
            </p>
          </div>
          <div className="p-4 bg-dark-800 rounded-[0.4375rem]">
            <p className="mb-3 text-14 font-medium text-white/55">{t("faction.onlineIn7Days")}</p>
            <p className="text-20 font-bold">{rowInfo?.onlineTimeInAWeek}</p>
          </div>
          <div className="p-4 bg-dark-800 rounded-[0.4375rem]">
            <p className="mb-3 text-14 font-medium text-white/55">
              {t("faction.onlineForAllTime")}
            </p>
            <p className="text-20 font-bold">Нет поля</p>
          </div>
          <div className="p-4 bg-dark-800 rounded-[0.4375rem]">
            <p className="mb-3 text-14 font-medium text-white/55">
              {t("faction.workingHoursIn7Days")}
            </p>
            <p className="text-20 font-bold">Нет поля</p>
          </div>
          <div className="p-4 bg-dark-800 rounded-[0.4375rem]">
            <p className="mb-3 text-14 font-medium text-white/55">
              {t("faction.generalWorkingHours")}
            </p>
            <p className="text-20 font-bold">Нет поля</p>
          </div>
        </div>
        <FactionExperience
          src={factionsList[faction].experienceSrc}
          currentCount={1500}
          nextLevelCount={3000}
          count={15}
          onClick={onUpRank}
          factionName={faction}
          className="mt-auto mb-5"
        />
        <div className="grid grid-cols-3 gap-y-2.5 gap-x-3 mb-3.5">
          {actionButtons.map((item, idx) => (
            <Button
              key={item}
              onClick={actionsList[idx]}
              variant="solid"
              className="grow h-9.5 p-3 text-12 bg-dark-800 rounded-[0.5625rem] hover:brightness-110"
            >
              {item}
            </Button>
          ))}
        </div>
        <Button
          className="gap-2 h-11 bg-gradient-red hover:brightness-105"
          onClick={onDismiss}
          variant="solid"
        >
          <Exit3Icon className="size-4" /> {t("faction.dismiss")}
        </Button>
      </div>
      {modals}
    </div>
  );
}
